import React, { useState, useEffect, useRef } from 'react';
import sdApi from './sdApi';

function Img2Img({ onImageGenerated, loras = [], onRefreshLoras, isLoadingLoras }) {
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [steps, setSteps] = useState(20);
  const [cfgScale, setCfgScale] = useState(7);
  const [denoisingStrength, setDenoisingStrength] = useState(0.75);
  const [width, setWidth] = useState(512);
  const [height, setHeight] = useState(512);
  const [seed, setSeed] = useState(-1);
  const [sampler, setSampler] = useState('Euler a');
  const [samplers, setSamplers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sourceImage, setSourceImage] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedLora, setSelectedLora] = useState('');
  const [loraWeight, setLoraWeight] = useState(0.8);

  useEffect(() => {
    sdApi.getSamplers().then(data => setSamplers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(async () => {
        try {
          const progressData = await sdApi.getProgress();
          setProgress(Math.round(progressData.progress * 100));
        } catch (err) {}
      }, 500);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setSourceImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => setSourceImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGenerate = async () => {
    if (!sourceImage) {
      setError('请先上传一张图片');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const base64Data = sourceImage.split(',')[1];
      const result = await sdApi.img2img({
        prompt,
        negative_prompt: negativePrompt,
        init_images: [base64Data],
        steps,
        cfg_scale: cfgScale,
        width,
        height,
        denoising_strength: denoisingStrength,
        seed,
        sampler_name: sampler,
      });

      if (result.images && result.images.length > 0) {
        const imageData = `data:image/png;base64,${result.images[0]}`;
        setGeneratedImage(imageData);
        if (onImageGenerated) {
          onImageGenerated({
            image: imageData,
            info: JSON.parse(result.info),
            prompt,
            timestamp: new Date().toISOString(),
          });
        }
      }
    } catch (err) {
      setError(`生成失败: ${err.message}`);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const handleInterrupt = async () => {
    try { await sdApi.interrupt(); } catch (err) {}
  };

  const handleAddLora = () => {
    if (!selectedLora) return;
    const loraTag = `<lora:${selectedLora}:${loraWeight}>`;
    if (!prompt.includes(`<lora:${selectedLora}:`)) {
      setPrompt(prev => prev ? `${prev} ${loraTag}` : loraTag);
    }
  };

  const handleRemoveLora = (loraName) => {
    const loraRegex = new RegExp(`\\s*<lora:${loraName}:[^>]+>`, 'g');
    setPrompt(prev => prev.replace(loraRegex, '').trim());
  };

  const getUsedLoras = () => {
    const loraRegex = /<lora:([^:]+):([^>]+)>/g;
    const matches = [];
    let match;
    while ((match = loraRegex.exec(prompt)) !== null) {
      matches.push({ name: match[1], weight: parseFloat(match[2]) });
    }
    return matches;
  };

  const usedLoras = getUsedLoras();

  return (
    <div className="sd-generator-panel">
      <div className="sd-panel-header">
        <h2>🖼️ 图生图 Image to Image</h2>
      </div>

      <div className="sd-generator-content">
        <div className="sd-input-section">
          <div className="sd-input-group">
            <label>源图片</label>
            <div
              className="sd-upload-area"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {sourceImage ? (
                <img src={sourceImage} alt="Source" className="sd-source-preview" />
              ) : (
                <div className="sd-upload-placeholder">
                  <span>📤</span>
                  <p>点击或拖拽上传图片</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
          </div>

          <div className="sd-input-group">
            <label>提示词 Prompt</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="描述你想要的变化..." rows={3} />
          </div>

          <div className="sd-input-group">
            <label>负面提示词 Negative Prompt</label>
            <textarea value={negativePrompt} onChange={(e) => setNegativePrompt(e.target.value)} placeholder="描述你不想要的内容..." rows={2} />
          </div>

          <div className="sd-input-group sd-lora-section">
            <div className="sd-lora-header">
              <label>🎭 Lora</label>
              <button className="sd-refresh-btn" onClick={onRefreshLoras} disabled={isLoadingLoras}>
                {isLoadingLoras ? '⏳' : '🔄'}
              </button>
            </div>
            <div className="sd-lora-selector">
              <select value={selectedLora} onChange={(e) => setSelectedLora(e.target.value)}>
                <option value="">选择 Lora...</option>
                {loras.map(lora => <option key={lora.name} value={lora.name}>{lora.name}</option>)}
              </select>
              <div className="sd-lora-weight">
                <label>权重: {loraWeight}</label>
                <input type="range" min="0" max="2" step="0.1" value={loraWeight} onChange={(e) => setLoraWeight(parseFloat(e.target.value))} />
              </div>
              <button className="sd-add-lora-btn" onClick={handleAddLora} disabled={!selectedLora}>➕ 添加</button>
            </div>
            {usedLoras.length > 0 && (
              <div className="sd-used-loras">
                <label>已使用的 Lora:</label>
                <div className="sd-lora-tags">
                  {usedLoras.map(lora => (
                    <span key={lora.name} className="sd-lora-tag">
                      {lora.name} ({lora.weight})
                      <button onClick={() => handleRemoveLora(lora.name)}>✕</button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="sd-params-grid">
            <div className="sd-input-group">
              <label>采样器</label>
              <select value={sampler} onChange={(e) => setSampler(e.target.value)}>
                {samplers.length > 0 ? samplers.map(s => <option key={s.name} value={s.name}>{s.name}</option>) : (
                  <>
                    <option value="Euler a">Euler a</option>
                    <option value="Euler">Euler</option>
                    <option value="DPM++ 2M">DPM++ 2M</option>
                  </>
                )}
              </select>
            </div>

            <div className="sd-input-group">
              <label>重绘幅度: {denoisingStrength}</label>
              <input type="range" min="0" max="1" step="0.05" value={denoisingStrength} onChange={(e) => setDenoisingStrength(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>步数 Steps: {steps}</label>
              <input type="range" min="1" max="150" value={steps} onChange={(e) => setSteps(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>CFG Scale: {cfgScale}</label>
              <input type="range" min="1" max="30" step="0.5" value={cfgScale} onChange={(e) => setCfgScale(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>宽度: {width}px</label>
              <input type="range" min="256" max="1024" step="64" value={width} onChange={(e) => setWidth(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>高度: {height}px</label>
              <input type="range" min="256" max="1024" step="64" value={height} onChange={(e) => setHeight(Number(e.target.value))} />
            </div>

            <div className="sd-input-group">
              <label>种子 Seed</label>
              <input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} placeholder="-1 为随机" />
            </div>
          </div>

          {error && <div className="sd-error">{error}</div>}

          <div className="sd-action-buttons">
            <button className="sd-generate-btn" onClick={handleGenerate} disabled={loading || !sourceImage}>
              {loading ? `生成中... ${progress}%` : '🎨 生成图像'}
            </button>
            {loading && <button className="sd-interrupt-btn" onClick={handleInterrupt}>⏹ 中断</button>}
          </div>

          {loading && (
            <div className="sd-progress-bar">
              <div className="sd-progress-fill" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>

        <div className="sd-preview-section">
          <div className="sd-preview-container">
            {generatedImage ? (
              <img src={generatedImage} alt="Generated" />
            ) : (
              <div className="sd-preview-placeholder">
                <span>🖼️</span>
                <p>生成的图像将显示在这里</p>
              </div>
            )}
          </div>
          {generatedImage && (
            <a href={generatedImage} download={`sd-img2img-${Date.now()}.png`} className="sd-download-btn">
              📥 下载图像
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default Img2Img;

